import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { importFreeTimerEntries } from "@/utils/freeTimerImport";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import UpgradeModal from "@/components/UpgradeModal";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Clock, Play, Square, Plus, Trash2, Edit, CheckCircle, XCircle, Copy,
  Pause, ChevronDown, ChevronRight, ChevronLeft, Star, Keyboard, Target,
  Zap, List, LayoutGrid, FileText
} from "lucide-react";
import { format, addDays, startOfWeek, addWeeks, isSameDay } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { TIMER_EVENTS } from "@/components/TimerWidget";
import TimeEntryDefaults from "@/utils/timeEntryDefaults";
import PauseEditor from "@/components/ui/PauseEditor";
import WeekCalendar from "@/components/time-tracking/WeekCalendar";

// ─── Helpers ─────────────────────────────────────────
const STORAGE_TEMPLATES_KEY = "ivoliq_time_templates";
const STORAGE_WEEKLY_TARGET_KEY = "ivoliq_weekly_target";

const PROJECT_COLORS = [
  { dot: "bg-blue-500", badge: "bg-blue-100 text-blue-700" },
  { dot: "bg-emerald-500", badge: "bg-emerald-100 text-emerald-700" },
  { dot: "bg-purple-500", badge: "bg-purple-100 text-purple-700" },
  { dot: "bg-orange-500", badge: "bg-orange-100 text-orange-700" },
  { dot: "bg-pink-500", badge: "bg-pink-100 text-pink-700" },
  { dot: "bg-teal-500", badge: "bg-teal-100 text-teal-700" },
  { dot: "bg-indigo-500", badge: "bg-indigo-100 text-indigo-700" },
  { dot: "bg-yellow-500", badge: "bg-yellow-100 text-yellow-700" },
];

const colorCache = {};
const getProjectColor = (pid) => {
  if (!colorCache[pid]) colorCache[pid] = PROJECT_COLORS[Object.keys(colorCache).length % PROJECT_COLORS.length];
  return colorCache[pid];
};

const fmtMin = (m) => { if (!m) return "0h"; const h = Math.floor(m/60); const mm = Math.round(m%60); return mm > 0 ? `${h}h ${mm}min` : `${h}h`; };
const fmtDur = (s) => `${String(Math.floor(s/3600)).padStart(2,"0")}:${String(Math.floor((s%3600)/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;

const loadTemplates = () => { try { return JSON.parse(localStorage.getItem(STORAGE_TEMPLATES_KEY)||"[]"); } catch { return []; } };
const saveTemplatesLS = (t) => localStorage.setItem(STORAGE_TEMPLATES_KEY, JSON.stringify(t));
const loadWeeklyTarget = () => { try { return parseInt(localStorage.getItem(STORAGE_WEEKLY_TARGET_KEY)||"0",10); } catch { return 0; } };
const saveWeeklyTargetLS = (h) => localStorage.setItem(STORAGE_WEEKLY_TARGET_KEY, String(h));

// ─── Main ────────────────────────────────────────────
export default function TimeTracking() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locale = i18n.language === "de" ? de : enUS;
  const qaRef = useRef(null);

  const [projects, setProjects] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [activeTimer, setActiveTimer] = useState(null);
  const [timerDuration, setTimerDuration] = useState(0);
  const [showBilled, setShowBilled] = useState("all");
  const [collapsedDays, setCollapsedDays] = useState(new Set());
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, entryId: null });

  const [weekOffset, setWeekOffset] = useState(0);
  const [viewMode, setViewModeState] = useState(() => localStorage.getItem("tt_view_mode") || "list");
  const setViewMode = useCallback((mode) => {
    setViewModeState(mode);
    try { localStorage.setItem("tt_view_mode", mode); } catch (_) {}
  }, []);
  const [templates, setTemplates] = useState(loadTemplates());
  const [weeklyTarget, setWeeklyTarget] = useState(loadWeeklyTarget());
  const [showTargetEditor, setShowTargetEditor] = useState(false);
  const [targetInput, setTargetInput] = useState(weeklyTarget);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);

  // Dialog form
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formDate, setFormDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [formStartTime, setFormStartTime] = useState("09:00");
  const [formEndTime, setFormEndTime] = useState("17:00");
  const [formProjectId, setFormProjectId] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [showPauseEditor, setShowPauseEditor] = useState(false);
  const [pauses, setPauses] = useState([]);
  const [pauseErrors, setPauseErrors] = useState({});

  // Quick-add
  const [qaProjectId, setQaProjectId] = useState("");
  const [qaDescription, setQaDescription] = useState("");
  const [qaStartTime, setQaStartTime] = useState("09:00");
  const [qaEndTime, setQaEndTime] = useState("17:00");

  // ─── Fetching ──────────────────────────────────────
  const fetchProjects = async () => {
    try { const r = await api.get("/projects"); setProjects(r.data.filter(p=>p.status==="active")); }
    catch (err) { console.warn('[TimeTracking] fetchProjects failed:', err?.response?.status, err?.message); }
  };
  const fetchTimeEntries = async () => {
    try { const r = await api.get("/time-entries"); setTimeEntries(r.data.sort((a,b)=>new Date(b.start_time)-new Date(a.start_time))); }
    catch (err) { console.warn('[TimeTracking] fetchTimeEntries failed:', err?.response?.status, err?.message); }
  };
  const fetchActiveTimer = async () => {
    try { const r = await api.get("/time-entries/active"); setActiveTimer(r.data); }
    catch (err) {
      // 404 ist erwartet wenn kein Timer läuft — nur bei 5xx loggen
      if (err?.response?.status >= 500) console.warn('[TimeTracking] fetchActiveTimer failed:', err?.response?.status, err?.message);
    }
  };

  useEffect(() => {
    Promise.all([fetchProjects(), fetchTimeEntries(), fetchActiveTimer()]).then(async () => {
      const res = await importFreeTimerEntries();
      if (res?.imported > 0) Promise.all([fetchProjects(), fetchTimeEntries()]);
    });
    const onStop = () => { setActiveTimer(null); setTimerDuration(0); fetchTimeEntries(); };
    const onUpd = (e) => setActiveTimer(e.detail);
    window.addEventListener(TIMER_EVENTS.TIMER_STOPPED, onStop);
    window.addEventListener(TIMER_EVENTS.TIMER_UPDATED, onUpd);
    window.addEventListener(TIMER_EVENTS.TIMER_PAUSED, onUpd);
    window.addEventListener(TIMER_EVENTS.TIMER_RESUMED, onUpd);
    return () => { window.removeEventListener(TIMER_EVENTS.TIMER_STOPPED, onStop); window.removeEventListener(TIMER_EVENTS.TIMER_UPDATED, onUpd); window.removeEventListener(TIMER_EVENTS.TIMER_PAUSED, onUpd); window.removeEventListener(TIMER_EVENTS.TIMER_RESUMED, onUpd); };
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("create")==="1") { openNewDialog(); p.delete("create"); navigate({search:p.toString()?`?${p.toString()}`:""},{ replace:true }); }
  }, [location.search]);

  useEffect(() => {
    if (!activeTimer || activeTimer.is_paused) return;
    const iv = setInterval(() => {
      const el = Math.floor((Date.now() - new Date(activeTimer.start_time).getTime())/1000);
      const ps = Math.round((activeTimer.total_pause_duration||0)*60);
      setTimerDuration(Math.max(0, el - ps));
    }, 1000);
    return () => clearInterval(iv);
  }, [activeTimer]);

  // Keyboard shortcuts
  useEffect(() => {
    const h = (e) => {
      if (["INPUT","TEXTAREA","SELECT"].includes(e.target.tagName) || e.target.isContentEditable || e.metaKey || e.ctrlKey) return;
      if (e.key==="n") { e.preventDefault(); setShowQuickAdd(true); setTimeout(()=>qaRef.current?.focus(),100); }
      else if (e.key==="t") { e.preventDefault(); if(activeTimer) stopTimer(); }
      else if (e.key==="?") { e.preventDefault(); setShowShortcuts(p=>!p); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [activeTimer]);

  // ─── Timer actions ─────────────────────────────────
  const startTimer = async (projectId, desc="") => {
    try { const r = await api.post(`/time-entries/start?project_id=${projectId}&description=${encodeURIComponent(desc)}`); setActiveTimer(r.data); toast.success("Timer gestartet"); fetchTimeEntries(); const p=projects.find(p=>p.id===projectId); if(p) TimeEntryDefaults.saveLastUsedProject(projectId,p.name); window.dispatchEvent(new CustomEvent(TIMER_EVENTS.TIMER_STARTED,{detail:r.data})); }
    catch(e) { if(e.isGuestReadonly) return; if(e.response?.status===403){setUpgradeMessage(e.response.data.detail);setUpgradeModalOpen(true);} else toast.error(e.response?.data?.detail||"Fehler"); }
  };
  const stopTimer = async () => {
    try { await api.post("/time-entries/stop"); setActiveTimer(null); setTimerDuration(0); toast.success("Timer gestoppt"); fetchTimeEntries(); window.dispatchEvent(new CustomEvent(TIMER_EVENTS.TIMER_STOPPED)); }
    catch(e) { if(e.isGuestReadonly) return; if(e.response?.status===403){setUpgradeMessage(e.response.data.detail);setUpgradeModalOpen(true);} else toast.error("Fehler"); }
  };
  const pauseTimer = async () => { if(!activeTimer) return; try { const r=await api.post(`/time-entries/${activeTimer.id}/pause`); setActiveTimer(r.data); toast.success("Pausiert"); window.dispatchEvent(new CustomEvent(TIMER_EVENTS.TIMER_PAUSED,{detail:r.data})); } catch(e) { if(e.isGuestReadonly) return; toast.error("Fehler"); } };
  const resumeTimer = async () => { if(!activeTimer) return; try { const r=await api.post(`/time-entries/${activeTimer.id}/resume`); setActiveTimer(r.data); toast.success("Fortgesetzt"); window.dispatchEvent(new CustomEvent(TIMER_EVENTS.TIMER_RESUMED,{detail:r.data})); } catch(e) { if(e.isGuestReadonly) return; toast.error("Fehler"); } };

  // ─── Dialog ────────────────────────────────────────
  const openNewDialog = () => {
    const d = TimeEntryDefaults.getManualEntryDefaults(projects);
    setEditingEntry(null); setFormDate(format(new Date(),"yyyy-MM-dd")); setFormStartTime("09:00"); setFormEndTime("17:00");
    setFormProjectId(d.project_id||""); setFormDescription(""); setShowPauseEditor(false); setPauses([]); setPauseErrors({}); setDialogOpen(true);
  };

  const handleDialogSubmit = async (e) => {
    e.preventDefault();
    const st = `${formDate}T${formStartTime}`, en = `${formDate}T${formEndTime}`;
    if (!formProjectId) { toast.error("Bitte Projekt wählen"); return; }
    if (new Date(en) <= new Date(st)) { toast.error("Endzeit muss nach Startzeit liegen"); return; }
    if (pauses.length > 0) { const bm=(new Date(en)-new Date(st))/60000; const pm=pauses.reduce((s,p)=>s+(p||0),0); if(pm>bm){toast.error("Pausen > Arbeitszeit");return;} }
    try {
      const data = { project_id:formProjectId, description:formDescription, start_time:new Date(st).toISOString(), end_time:new Date(en).toISOString() };
      let eid;
      if (editingEntry) { await api.put(`/time-entries/${editingEntry.id}`,data); eid=editingEntry.id; toast.success("Aktualisiert"); }
      else { const r=await api.post("/time-entries",data); eid=r.data.id; toast.success("Erstellt"); const p=projects.find(p=>p.id===data.project_id); if(p) TimeEntryDefaults.saveLastUsedProject(data.project_id,p.name); }
      if (pauses.length>0 && pauses.some(p=>p>0)) { const vp=pauses.filter(p=>p>0); if(vp.length>0) await api.put(`/time-entries/${eid}/manual-pauses`,{pauses:vp.map(d=>({duration_minutes:d}))}); }
      setDialogOpen(false); fetchTimeEntries();
    } catch(e) { if(e.response?.status===403){setUpgradeMessage(e.response.data.detail);setUpgradeModalOpen(true);} else toast.error(e.response?.data?.detail || "Fehler"); }
  };

  // ─── Quick-Add ─────────────────────────────────────
  const handleQuickAdd = async () => {
    if (!qaProjectId) { toast.error("Bitte Projekt wählen"); return; }
    const today=format(new Date(),"yyyy-MM-dd");
    const si=new Date(`${today}T${qaStartTime}`).toISOString(), ei=new Date(`${today}T${qaEndTime}`).toISOString();
    if (new Date(ei)<=new Date(si)) { toast.error("Endzeit muss nach Startzeit liegen"); return; }
    try { await api.post("/time-entries",{project_id:qaProjectId,description:qaDescription,start_time:si,end_time:ei}); toast.success("Erstellt"); setQaDescription(""); setShowQuickAdd(false); fetchTimeEntries(); }
    catch(e) { if(e.response?.status===403){setUpgradeMessage(e.response.data.detail);setUpgradeModalOpen(true);} else toast.error("Fehler"); }
  };

  // ─── Templates ─────────────────────────────────────
  const addTemplate = (entry) => {
    if (templates.find(t=>t.project_id===entry.project_id && t.description===(entry.description||""))) { toast.info("Vorlage existiert"); return; }
    const tpl = { id:crypto.randomUUID(), project_id:entry.project_id, project_name:entry.project_name||"", description:entry.description||"", duration_minutes:entry.net_duration_minutes||entry.duration_minutes||60 };
    const n=[...templates,tpl]; setTemplates(n); saveTemplatesLS(n); toast.success("Vorlage gespeichert");
  };
  const removeTemplate = (id) => { const n=templates.filter(t=>t.id!==id); setTemplates(n); saveTemplatesLS(n); };
  const applyTemplate = async (tpl) => {
    const today=format(new Date(),"yyyy-MM-dd"); const s=new Date(`${today}T09:00`); const e=new Date(s.getTime()+(tpl.duration_minutes||60)*60000);
    try { await api.post("/time-entries",{project_id:tpl.project_id,description:tpl.description,start_time:s.toISOString(),end_time:e.toISOString()}); toast.success(`"${tpl.description||tpl.project_name}" eingetragen`); fetchTimeEntries(); }
    catch(e) { toast.error("Fehler"); }
  };

  // ─── Duplicate ─────────────────────────────────────
  const duplicateDay = async (src, tgt) => {
    try { const r=await api.post("/time-entries/duplicate-day",{source_date:format(src,"yyyy-MM-dd"),target_date:format(tgt,"yyyy-MM-dd")}); toast.success(r.data.message); fetchTimeEntries(); }
    catch(e) { toast.error(e.response?.data?.detail||"Fehler"); }
  };
  const duplicateWeek = async (targetMonday) => {
    const srcMonday = startOfWeek(addWeeks(new Date(), weekOffset), { weekStartsOn: 1 });
    try { const r=await api.post("/time-entries/duplicate-week",{source_week_start:format(srcMonday,"yyyy-MM-dd"),target_week_start:format(startOfWeek(targetMonday,{weekStartsOn:1}),"yyyy-MM-dd")}); toast.success(r.data.message); fetchTimeEntries(); }
    catch(e) { toast.error(e.response?.data?.detail||"Fehler"); }
  };

  // ─── Entry actions ─────────────────────────────────
  const deleteEntry = async (id) => { try { await api.delete(`/time-entries/${id}`); toast.success("Gelöscht"); fetchTimeEntries(); setConfirmDialog({isOpen:false,entryId:null}); } catch { toast.error("Fehler"); } };
  const duplicateEntry = (entry) => {
    setEditingEntry(null); setFormDate(format(new Date(),"yyyy-MM-dd")); setFormStartTime(format(new Date(entry.start_time),"HH:mm")); setFormEndTime(format(new Date(entry.end_time),"HH:mm"));
    setFormProjectId(entry.project_id); setFormDescription(entry.description||""); setShowPauseEditor(false); setPauses([]); setPauseErrors({}); setDialogOpen(true);
  };
  const editEntry = (entry) => {
    const s=new Date(entry.start_time), e=entry.end_time?new Date(entry.end_time):null;
    setEditingEntry(entry); setFormDate(format(s,"yyyy-MM-dd")); setFormStartTime(format(s,"HH:mm")); setFormEndTime(e?format(e,"HH:mm"):"");
    setFormProjectId(entry.project_id); setFormDescription(entry.description||"");
    if(entry.pauses?.length>0){setPauses(entry.pauses.map(p=>p.duration_minutes));setShowPauseEditor(true);}else{setPauses([]);setShowPauseEditor(false);}
    setPauseErrors({}); setDialogOpen(true);
  };

  // Calendar handlers
  const handleCalendarEntryUpdate = async (id,data) => { try { await api.put(`/time-entries/${id}`,data); toast.success("Verschoben"); fetchTimeEntries(); } catch { toast.error("Fehler"); } };
  const handleCalendarEntryCreate = (s,e) => {
    setEditingEntry(null); setFormDate(format(s,"yyyy-MM-dd")); setFormStartTime(format(s,"HH:mm")); setFormEndTime(format(e,"HH:mm"));
    setFormProjectId(""); setFormDescription(""); setShowPauseEditor(false); setPauses([]); setPauseErrors({}); setDialogOpen(true);
  };

  // ─── Computed ──────────────────────────────────────
  const stats = (() => {
    const b=timeEntries.filter(e=>e.invoiced&&(e.net_duration_minutes||e.duration_minutes));
    const u=timeEntries.filter(e=>!e.invoiced&&(e.net_duration_minutes||e.duration_minutes));
    return { bH:(b.reduce((s,e)=>s+(e.net_duration_minutes||e.duration_minutes),0)/60).toFixed(1), uH:(u.reduce((s,e)=>s+(e.net_duration_minutes||e.duration_minutes),0)/60).toFixed(1), bC:b.length, uC:u.length };
  })();
  const totalH = (parseFloat(stats.bH)+parseFloat(stats.uH)).toFixed(1);

  // Project summaries for unbilled entries (for "Rechnung erstellen" feature)
  const projectSummaries = (() => {
    const unbilled = timeEntries.filter(e => !e.invoiced && (e.net_duration_minutes || e.duration_minutes));
    const byProject = {};
    unbilled.forEach(e => {
      if (!byProject[e.project_id]) byProject[e.project_id] = { name: e.project_name, minutes: 0, count: 0 };
      byProject[e.project_id].minutes += e.net_duration_minutes || e.duration_minutes || 0;
      byProject[e.project_id].count++;
    });
    return Object.entries(byProject).map(([id, data]) => ({ id, ...data })).sort((a, b) => b.minutes - a.minutes);
  })();

  const filtered = showBilled==="billed"?timeEntries.filter(e=>e.invoiced):showBilled==="unbilled"?timeEntries.filter(e=>!e.invoiced):timeEntries;
  const grouped = (() => {
    const g={}; filtered.forEach(e=>{const dk=format(new Date(e.start_time),"yyyy-MM-dd"); if(!g[dk])g[dk]={dateKey:dk,date:new Date(e.start_time),entries:[],totalMin:0}; g[dk].entries.push(e); g[dk].totalMin+=e.net_duration_minutes||e.duration_minutes||0;});
    return Object.values(g).sort((a,b)=>b.date-a.date);
  })();

  const weekData = (() => {
    const ref=addWeeks(new Date(),weekOffset); const mon=startOfWeek(ref,{weekStartsOn:1});
    const days=Array.from({length:7},(_,i)=>{const d=addDays(mon,i);return{date:d,label:format(d,"EEE",{locale:de}),min:0};});
    timeEntries.forEach(e=>{const ed=new Date(e.start_time);days.forEach(d=>{if(isSameDay(d.date,ed))d.min+=e.net_duration_minutes||e.duration_minutes||0;});});
    return{days,total:days.reduce((s,d)=>s+d.min,0),monday:mon,kw:format(mon,"ww",{locale:de})};
  })();

  const bruttoMin = (() => { if(formStartTime&&formEndTime){const d=(new Date(`${formDate}T${formEndTime}`)-new Date(`${formDate}T${formStartTime}`))/60000; return d>0?d:0;} return 0; })();

  // Offene Stunden nach Projekt + "Rechnung erstellen" — in beiden Ansichten (Liste & Kalender) sichtbar
  const projectSummaryBlock = projectSummaries.length > 0 && (
    <div className="bg-orange-50/60 border border-orange-200 rounded-lg p-3 mb-3" data-testid="unbilled-project-summary">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-orange-700">Offene Stunden nach Projekt</span>
        <span className="text-xs text-orange-500">{stats.uH}h offen</span>
      </div>
      <div className="space-y-1.5">
        {projectSummaries.map(p => {
          const c = getProjectColor(p.id);
          return (
            <div key={p.id} className="flex items-center justify-between bg-white rounded-md px-3 py-2 border border-orange-100" data-testid={`project-summary-${p.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <span className={`flex-shrink-0 w-2 h-2 rounded-full ${c.dot}`} />
                <span className="text-sm font-medium text-gray-800 truncate">{p.name}</span>
                <span className="text-xs text-gray-400 flex-shrink-0">{p.count} {p.count === 1 ? 'Eintrag' : 'Einträge'} · {fmtMin(p.minutes)}</span>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 flex-shrink-0 ml-2"
                onClick={() => navigate(`/invoices?auto_project=${p.id}`)}
                data-testid={`create-invoice-project-${p.id}`}
              >
                <FileText className="h-3 w-3 mr-1" />Rechnung erstellen
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ─── RENDER ────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="time-tracking-page">
      {/* ─── Header: clean, compact ─── */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Zeiterfassung</h1>
          <div className="hidden sm:flex items-center gap-3 text-sm text-gray-500">
            <span>{totalH}h gesamt</span>
            <span className="text-gray-300">·</span>
            <span className="text-orange-600">{stats.uC} offen</span>
            {parseFloat(stats.bH) > 0 && <>
              <span className="text-gray-300">·</span>
              <span className="text-green-600">{stats.bH}h abgerechnet</span>
            </>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button onClick={()=>setViewMode("list")} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode==="list"?"bg-white shadow-sm text-gray-900":"text-gray-500 hover:text-gray-700"}`} data-testid="view-list-btn">
              <List className="h-3.5 w-3.5 inline mr-1" />Liste
            </button>
            <button onClick={()=>setViewMode("calendar")} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode==="calendar"?"bg-white shadow-sm text-gray-900":"text-gray-500 hover:text-gray-700"}`} data-testid="view-calendar-btn">
              <LayoutGrid className="h-3.5 w-3.5 inline mr-1" />Kalender
            </button>
          </div>
          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={()=>setShowShortcuts(true)} title="Tastenkürzel (?)">
            <Keyboard className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="outline" className="h-8" onClick={()=>{setShowQuickAdd(p=>!p);if(!showQuickAdd)setTimeout(()=>qaRef.current?.focus(),100);}} data-testid="toggle-quick-add">
            <Zap className="h-3.5 w-3.5 mr-1" />Schnell
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="h-8" data-testid="add-time-entry-button" onClick={openNewDialog}>
                <Plus className="h-3.5 w-3.5 mr-1" />Eintrag
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editingEntry?"Eintrag bearbeiten":"Neuer Eintrag"}</DialogTitle><DialogDescription className="sr-only">Projekt, Beschreibung, Datum, Start- und Endzeit sowie Pausen des Zeiteintrags bearbeiten.</DialogDescription></DialogHeader>
              <form onSubmit={handleDialogSubmit} className="space-y-4">
                <div><Label className="text-sm font-medium">Projekt</Label>
                  <Select value={formProjectId} onValueChange={setFormProjectId}><SelectTrigger data-testid="dialog-project-select"><SelectValue placeholder="Projekt wählen..." /></SelectTrigger>
                    <SelectContent>{projects.map(p=><SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-sm font-medium">Beschreibung</Label>
                  <Input data-testid="dialog-description" value={formDescription} onChange={e=>setFormDescription(e.target.value)} placeholder="Was hast du gemacht?" /></div>
                <div><Label className="text-sm font-medium">Datum</Label>
                  <Input type="date" data-testid="dialog-date" value={formDate} onChange={e=>setFormDate(e.target.value)} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-sm font-medium">Startzeit</Label><Input type="time" data-testid="dialog-start-time" value={formStartTime} onChange={e=>setFormStartTime(e.target.value)} /></div>
                  <div><Label className="text-sm font-medium">Endzeit</Label><Input type="time" data-testid="dialog-end-time" value={formEndTime} onChange={e=>setFormEndTime(e.target.value)} /></div>
                </div>
                {bruttoMin>0&&<div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">Dauer: <strong>{fmtMin(bruttoMin)}</strong>
                  {pauses.length>0&&pauses.some(p=>p>0)&&<span className="ml-2">(Netto: {fmtMin(bruttoMin-pauses.reduce((s,p)=>s+(p||0),0))})</span>}</div>}
                {bruttoMin>0&&<div><Button type="button" variant="outline" size="sm" onClick={()=>setShowPauseEditor(!showPauseEditor)} className="w-full">{showPauseEditor?"Pausen ausblenden":"Pausen hinzufügen"}</Button>
                  {showPauseEditor&&<div className="mt-2 max-h-48 overflow-y-auto"><PauseEditor pauses={pauses} onPausesChange={np=>{setPauses(np);const tot=np.reduce((s,p)=>s+(p||0),0);setPauseErrors(tot>bruttoMin?{total:"Pausen > Arbeitszeit"}:{});}} bruttoMinutes={bruttoMin} errors={pauseErrors}/></div>}</div>}
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1" data-testid="save-time-entry-button" disabled={Object.keys(pauseErrors).length>0}>{editingEntry?"Aktualisieren":"Erstellen"}</Button>
                  {editingEntry && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" className="text-blue-600 border-blue-200 hover:bg-blue-50 px-3" title="Duplizieren auf..." data-testid="duplicate-entry-from-dialog">
                          <Copy className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-3" align="end">
                        <p className="text-sm font-medium mb-2">Duplizieren auf:</p>
                        <Calendar mode="single" locale={de} className="rounded-md border" onSelect={d => {
                          if (!d) return;
                          const targetDate = format(d, "yyyy-MM-dd");
                          const st = new Date(`${targetDate}T${formStartTime}`).toISOString();
                          const en = new Date(`${targetDate}T${formEndTime}`).toISOString();
                          api.post("/time-entries", { project_id: formProjectId, description: formDescription, start_time: st, end_time: en })
                            .then(() => { toast.success(`Dupliziert auf ${format(d, "d. MMM yyyy", { locale: de })}`); fetchTimeEntries(); setDialogOpen(false); })
                            .catch(() => toast.error("Fehler beim Duplizieren"));
                        }} />
                      </PopoverContent>
                    </Popover>
                  )}
                  {editingEntry && (
                    <Button type="button" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 px-3"
                      data-testid="delete-entry-from-dialog"
                      onClick={() => { setDialogOpen(false); setConfirmDialog({ isOpen: true, entryId: editingEntry.id }); }}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* ─── Quick-Add (collapsible, subtle) ─── */}
      {showQuickAdd && (
        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg animate-in fade-in slide-in-from-top-2 duration-200">
          <Select value={qaProjectId} onValueChange={setQaProjectId}>
            <SelectTrigger className="w-[160px] h-8 text-xs border-gray-200" data-testid="quick-add-project"><SelectValue placeholder="Projekt" /></SelectTrigger>
            <SelectContent>{projects.map(p=><SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          <Input ref={qaRef} value={qaDescription} onChange={e=>setQaDescription(e.target.value)} placeholder="Beschreibung..." className="flex-1 h-8 text-xs" data-testid="quick-add-description" onKeyDown={e=>{if(e.key==="Enter")handleQuickAdd();if(e.key==="Escape")setShowQuickAdd(false);}} />
          <Input type="time" value={qaStartTime} onChange={e=>setQaStartTime(e.target.value)} className="w-24 h-8 text-xs" data-testid="quick-add-start" />
          <span className="text-gray-300 text-xs">–</span>
          <Input type="time" value={qaEndTime} onChange={e=>setQaEndTime(e.target.value)} className="w-24 h-8 text-xs" data-testid="quick-add-end" />
          <Button onClick={handleQuickAdd} size="sm" className="h-8 px-3 text-xs" data-testid="quick-add-save">Speichern</Button>
          <button onClick={()=>setShowQuickAdd(false)} className="text-gray-400 hover:text-gray-600"><XCircle className="h-4 w-4" /></button>
        </div>
      )}

      {/* ─── Templates (only if exist) ─── */}
      {templates.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <Star className="h-3.5 w-3.5 text-amber-400" />
          {templates.map(tpl=>(
            <div key={tpl.id} className="flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-full pl-2.5 pr-1 py-0.5 text-[11px]">
              <button onClick={()=>applyTemplate(tpl)} className="text-amber-700 hover:text-amber-900 font-medium" data-testid={`template-${tpl.id}`}>
                {tpl.project_name}{tpl.description?`: ${tpl.description}`:""} · {fmtMin(tpl.duration_minutes)}
              </button>
              <button onClick={()=>removeTemplate(tpl.id)} className="text-amber-300 hover:text-red-500 p-0.5"><XCircle className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}

      {/* ─── Active Timer (compact) ─── */}
      {activeTimer ? (
        <div className="flex items-center gap-4 px-4 py-3 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg">
          <div className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${activeTimer.is_paused?"bg-yellow-500":"bg-red-500 animate-pulse"}`} />
          <div className="flex-1 min-w-0">
            <span className="font-semibold text-gray-900 text-sm">{activeTimer.project_name}</span>
            {activeTimer.description && <span className="text-gray-500 text-sm ml-2">{activeTimer.description}</span>}
          </div>
          <span className="text-2xl font-bold text-emerald-700 tabular-nums" data-testid="timer-display">{fmtDur(timerDuration)}</span>
          <div className="flex gap-1.5">
            <Button size="sm" className={`h-8 ${activeTimer.is_paused?"bg-emerald-600 hover:bg-emerald-700":"bg-yellow-600 hover:bg-yellow-700"}`} onClick={activeTimer.is_paused?resumeTimer:pauseTimer} data-testid="pause-resume-timer-button">
              {activeTimer.is_paused?<Play className="h-3.5 w-3.5" fill="white" />:<Pause className="h-3.5 w-3.5" fill="white" />}
            </Button>
            <Button size="sm" className="h-8 bg-red-600 hover:bg-red-700" onClick={stopTimer} data-testid="stop-timer-button">
              <Square className="h-3.5 w-3.5" fill="white" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
          <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
          <Select onValueChange={v=>startTimer(v,"")}>
            <SelectTrigger className="flex-1 h-8 border-0 bg-transparent shadow-none text-sm" data-testid="timer-project-select">
              <SelectValue placeholder="Projekt wählen und Timer starten..." />
            </SelectTrigger>
            <SelectContent>{projects.map(p=><SelectItem key={p.id} value={p.id}><div className="flex items-center"><Play className="h-3.5 w-3.5 mr-2 text-emerald-600" />{p.name}</div></SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}

      {/* ─── Week Bar (compact) ─── */}
      <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <button onClick={()=>setWeekOffset(weekOffset-1)} className="p-1 hover:bg-gray-100 rounded" data-testid="week-prev"><ChevronLeft className="h-4 w-4 text-gray-500" /></button>
            <span className="text-sm font-medium text-gray-700 min-w-[220px] text-center">
              KW {weekData.kw} · {format(weekData.monday,"d. MMM",{locale:de})} – {format(addDays(weekData.monday,6),"d. MMM",{locale:de})}
            </span>
            <button onClick={()=>setWeekOffset(weekOffset+1)} className="p-1 hover:bg-gray-100 rounded" data-testid="week-next"><ChevronRight className="h-4 w-4 text-gray-500" /></button>
            {weekOffset!==0&&<button onClick={()=>setWeekOffset(0)} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium ml-1">Heute</button>}
          </div>
          <div className="flex items-center gap-2">
            {weeklyTarget>0 && (
              <div className="flex items-center gap-2 mr-2">
                <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${weekData.total/60>=weeklyTarget?"bg-emerald-500":"bg-emerald-400"}`} style={{width:`${Math.min(100,(weekData.total/60/weeklyTarget)*100)}%`}} />
                </div>
                <span className="text-xs text-gray-500 tabular-nums">{(weekData.total/60).toFixed(1)}/{weeklyTarget}h</span>
              </div>
            )}
            <span className="text-sm font-semibold text-emerald-600">{fmtMin(weekData.total)}</span>
            {/* Woche kopieren mit Kalender-Picker */}
            <Popover>
              <PopoverTrigger asChild>
                <button className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600" title="Woche duplizieren" data-testid="duplicate-week-btn"><Copy className="h-3.5 w-3.5" /></button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-3" align="end">
                <p className="text-sm font-medium mb-2">Woche duplizieren auf:</p>
                <Calendar mode="single" onSelect={d=>{if(d)duplicateWeek(d);}} locale={de} className="rounded-md border" />
              </PopoverContent>
            </Popover>
            <Popover open={showTargetEditor} onOpenChange={setShowTargetEditor}>
              <PopoverTrigger asChild>
                <button className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600" data-testid="weekly-target-btn" title="Wochen-Ziel"><Target className="h-3.5 w-3.5" /></button>
              </PopoverTrigger>
              <PopoverContent className="w-44 p-3"><Label className="text-xs">Ziel (Stunden)</Label>
                <div className="flex gap-1.5 mt-1"><Input type="number" min="0" max="80" value={targetInput} onChange={e=>setTargetInput(parseInt(e.target.value)||0)} className="h-7 text-xs" />
                  <Button size="sm" className="h-7 text-xs" onClick={()=>{setWeeklyTarget(targetInput);saveWeeklyTargetLS(targetInput);setShowTargetEditor(false);}}>OK</Button></div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        {/* Mini bars */}
        <div className="grid grid-cols-7 gap-1">
          {weekData.days.map((day,i)=>{
            const isToday=isSameDay(day.date,new Date()); const max=Math.max(...weekData.days.map(d=>d.min),60); const bh=day.min>0?Math.max(3,Math.round((day.min/max)*28)):1;
            return(<div key={i} className="flex flex-col items-center gap-0.5">
              <span className={`text-[10px] font-medium ${isToday?"text-emerald-600":"text-gray-400"}`}>{day.label}</span>
              <div className="flex items-end h-7"><div style={{height:`${bh}px`}} className={`w-5 rounded-sm ${day.min>0?(isToday?"bg-emerald-500":"bg-emerald-200"):"bg-gray-100"}`}/></div>
              <span className={`text-[10px] ${isToday?"text-emerald-600 font-medium":"text-gray-400"}`}>{day.min>0?fmtMin(day.min):"—"}</span>
            </div>);
          })}
        </div>
      </div>

      {/* ─── Calendar View ─── */}
      {viewMode === "calendar" && (
        <>
          {projectSummaryBlock}
          <WeekCalendar entries={timeEntries.filter(e=>!e.is_running&&e.end_time)} weekMonday={weekData.monday} projects={projects}
            onEntryUpdate={handleCalendarEntryUpdate} onEntryCreate={handleCalendarEntryCreate} onEntryClick={editEntry} />
        </>
      )}

      {/* ─── List View ─── */}
      {viewMode === "list" && (
        <Tabs defaultValue="all" className="space-y-3" onValueChange={setShowBilled}>
          <TabsList><TabsTrigger value="all" data-testid="tab-all">Alle</TabsTrigger><TabsTrigger value="unbilled" data-testid="tab-unbilled">Offen ({stats.uC})</TabsTrigger><TabsTrigger value="billed" data-testid="tab-billed">Abgerechnet ({stats.bC})</TabsTrigger></TabsList>
          <TabsContent value={showBilled}>
            {/* Project summaries with "Rechnung erstellen" for unbilled entries — filter-unabhängig sichtbar (alle Tabs) */}
            {projectSummaryBlock}
            <div className="space-y-2">
              {grouped.length===0&&<div className="text-center py-12 text-gray-400 text-sm">Keine Einträge</div>}
              {grouped.map(({dateKey,date,entries,totalMin})=>{
                const isToday=dateKey===format(new Date(),"yyyy-MM-dd"); const isYest=dateKey===format(addDays(new Date(),-1),"yyyy-MM-dd");
                const collapsed=collapsedDays.has(dateKey); const lbl=isToday?"Heute":isYest?"Gestern":format(date,"EEEE, d. MMM yyyy",{locale:de});
                const pSum=entries.reduce((a,e)=>{if(!a[e.project_id])a[e.project_id]={n:e.project_name,m:0};a[e.project_id].m+=e.net_duration_minutes||e.duration_minutes||0;return a;},{});
                return(
                  <div key={dateKey} className={`bg-white border rounded-xl overflow-hidden ${isToday?"border-emerald-200":"border-gray-200"}`}>
                    <div className={`flex items-center justify-between px-3 py-2 ${isToday?"bg-emerald-50/50":""}`}>
                      <button className="flex items-center gap-2 flex-1 text-left" onClick={()=>setCollapsedDays(p=>{const n=new Set(p);n.has(dateKey)?n.delete(dateKey):n.add(dateKey);return n;})} data-testid={`day-group-${dateKey}`}>
                        {collapsed?<ChevronRight className="h-3.5 w-3.5 text-gray-400" />:<ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
                        <span className={`font-semibold text-sm ${isToday?"text-emerald-700":"text-gray-800"}`}>{lbl}</span>
                        <div className="flex items-center gap-1 ml-2">
                          {Object.entries(pSum).map(([pid,p])=>{const c=getProjectColor(pid);return<span key={pid} className="flex items-center gap-0.5"><span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}/><span className="text-[10px] text-gray-400">{p.n}</span></span>;})}
                        </div>
                      </button>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-bold ${isToday?"text-emerald-600":"text-gray-700"}`}>{fmtMin(totalMin)}</span>
                        <Popover><PopoverTrigger asChild><button className="p-1 hover:bg-gray-100 rounded text-gray-300 hover:text-gray-500" data-testid={`duplicate-day-${dateKey}`}><Copy className="h-3 w-3" /></button></PopoverTrigger>
                          <PopoverContent className="w-auto p-3" align="end"><p className="text-sm font-medium mb-2">Tag duplizieren auf:</p><Calendar mode="single" onSelect={d=>{if(d)duplicateDay(date,d);}} locale={de} className="rounded-md border" /></PopoverContent></Popover>
                      </div>
                    </div>
                    {!collapsed&&<div className="divide-y divide-gray-50 overflow-x-auto">
                      {entries.map(entry=>{const c=getProjectColor(entry.project_id);return(
                        <div key={entry.id} className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50/80 group min-w-[600px]" data-testid={`time-entry-${entry.id}`}>
                          <span className={`flex-shrink-0 w-1.5 h-1.5 rounded-full ${c.dot}`} />
                          <span className={`flex-shrink-0 text-[11px] font-medium px-1.5 py-0.5 rounded ${c.badge}`}>{entry.project_name}</span>
                          <span className="flex-1 text-sm text-gray-600 truncate">{entry.description||<span className="text-gray-300">—</span>}</span>
                          <span className="text-xs text-gray-400 tabular-nums">{format(new Date(entry.start_time),"HH:mm")} – {entry.end_time?format(new Date(entry.end_time),"HH:mm"):<span className="text-red-500 animate-pulse">●</span>}</span>
                          <span className="text-sm font-semibold text-emerald-600 w-14 text-right tabular-nums">{entry.duration_minutes?fmtMin(entry.net_duration_minutes||entry.duration_minutes):"—"}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${entry.invoiced?"bg-green-50 text-green-600":"bg-orange-50 text-orange-600"}`}>{entry.invoiced?"Abger.":"Offen"}</span>
                          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={()=>addTemplate(entry)} className="p-1 hover:bg-amber-50 rounded" title="Vorlage"><Star className="h-3 w-3 text-amber-400" /></button>
                            <button onClick={()=>duplicateEntry(entry)} className="p-1 hover:bg-blue-50 rounded" data-testid={`duplicate-entry-${entry.id}`}><Copy className="h-3 w-3 text-blue-500" /></button>
                            <button onClick={()=>editEntry(entry)} className="p-1 hover:bg-gray-100 rounded" data-testid={`edit-entry-${entry.id}`}><Edit className="h-3 w-3 text-gray-500" /></button>
                            <button onClick={async()=>{try{await api.patch(`/time-entries/${entry.id}/status?invoiced=${!entry.invoiced}`);toast.success(entry.invoiced?"Offen":"Abgerechnet");fetchTimeEntries();}catch{toast.error("Fehler");}}} className="p-1 hover:bg-gray-100 rounded" data-testid={`toggle-status-${entry.id}`}>
                              {entry.invoiced?<XCircle className="h-3 w-3 text-orange-500" />:<CheckCircle className="h-3 w-3 text-green-500" />}
                            </button>
                            <button onClick={()=>setConfirmDialog({isOpen:true,entryId:entry.id})} className="p-1 hover:bg-red-50 rounded" data-testid={`delete-entry-${entry.id}`}><Trash2 className="h-3 w-3 text-red-400" /></button>
                          </div>
                        </div>
                      );})}
                    </div>}
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      )}

      {/* Shortcuts modal */}
      <Dialog open={showShortcuts} onOpenChange={setShowShortcuts}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><Keyboard className="h-4 w-4" />Tastenkürzel</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {[["N","Schnelleintrag"],["T","Timer stoppen"],["?","Tastenkürzel"]].map(([k,d])=>(
              <div key={k} className="flex items-center justify-between"><span className="text-sm text-gray-600">{d}</span><kbd className="px-2 py-0.5 bg-gray-100 border rounded text-xs font-mono">{k}</kbd></div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <UpgradeModal isOpen={upgradeModalOpen} onClose={()=>setUpgradeModalOpen(false)} message={upgradeMessage} currentPlan={user?.plan} />
      <ConfirmDialog isOpen={confirmDialog.isOpen} onClose={()=>setConfirmDialog({isOpen:false,entryId:null})} onConfirm={()=>deleteEntry(confirmDialog.entryId)}
        title="Eintrag löschen?" description="Kann nicht rückgängig gemacht werden." confirmText="Löschen" cancelText="Abbrechen" variant="destructive" />
    </div>
  );
}
